import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { ClientsTable } from './clients-table.tsx'
import { MainContent } from '../../components/main-content.tsx'
import { MainContentHeader } from '../../components/main-content-header.tsx'
import { MainContentBody } from '../../components/main-content-body.tsx'
import { Layout } from '../../components/layout.tsx'
import {
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
} from '../../redux/hooks.ts'
import { ClientFormModal, ClientFormData } from '../../components/client-form-modal.tsx'
import { ConfirmDialog } from '../../components/confirm-dialog.tsx'

export const ClientsPage: FC = () => {
  const navigate = useNavigate()
  const [createClient] = useCreateClientMutation()
  const [updateClient] = useUpdateClientMutation()
  const [deleteClient] = useDeleteClientMutation()

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedClient, setSelectedClient] = useState<any>(null)

  const handleCreateClient = async (data: ClientFormData) => {
    await createClient(data).unwrap()
  }

  const handleUpdateClient = async (data: ClientFormData) => {
    if (!selectedClient) return
    await updateClient({
      id: selectedClient.id,
      data,
    }).unwrap()
  }

  const handleDeleteClient = async () => {
    if (!selectedClient) return
    await deleteClient(selectedClient.id).unwrap()
    setDeleteDialogOpen(false)
    setSelectedClient(null)
  }

  const handleView = (client: any) => {
    navigate(`/clients/${client.id}`)
  }

  const handleEdit = (client: any) => {
    setSelectedClient(client)
    setEditModalOpen(true)
  }

  const handleDelete = (client: any) => {
    setSelectedClient(client)
    setDeleteDialogOpen(true)
  }

  const handleToggleFavorite = async (client: any) => {
    await updateClient({
      id: client.id,
      data: { isFavorite: !client.isFavorite } as any,
    }).unwrap()
  }

  const handleToggleArchive = async (client: any) => {
    await updateClient({
      id: client.id,
      data: { isArchived: !client.isArchived } as any,
    }).unwrap()
  }

  return (
    <Layout>
      <MainContent>
        <MainContentHeader
          breadcrumbs={[{ label: 'Clients', href: '/clients' }]}
          buttons={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
            >
              Add Client
            </Button>
          }
        />
        <MainContentBody>
          <ClientsTable
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleFavorite={handleToggleFavorite}
            onToggleArchive={handleToggleArchive}
          />
        </MainContentBody>
      </MainContent>

      <ClientFormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateClient}
        title="Add New Client"
        submitText="Create Client"
      />

      <ClientFormModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false)
          setSelectedClient(null)
        }}
        onSubmit={handleUpdateClient}
        initialData={selectedClient}
        title="Edit Client"
        submitText="Update Client"
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setSelectedClient(null)
        }}
        onConfirm={handleDeleteClient}
        title="Delete Client"
        message={`Are you sure you want to delete "${selectedClient?.name}"? This action cannot be undone.`}
        confirmText="Delete"
      />
    </Layout>
  )
}
